import type {
  TimelineEvent,
  TimelineSummary,
  CommitTimelineEvent,
  ReviewTimelineEvent,
} from '../routes/route-payload-types'

export type TaggedEvent = {
  type: string
  event: TimelineEvent
}

export type EventGroup = {
  label: string
  type: string
  count: number
  events: TaggedEvent[]
  link?: string
  linkText?: string
}

/**
 * Build list of event groups to display.
 * Sorts timeline events into logical groups with chronological ordering within each group:
 * - Reviewer activity: approvals, changes requested, reviews, issue comments
 * - Code changes: commits, force pushes, base changes
 * - State changes: closed, reopened, draft status, merged
 * - Metadata changes: labels, review requests, description updates
 */
export function buildEventGroups(summary: TimelineSummary, changesUrl: string): EventGroup[] {
  const items: EventGroup[] = []

  // Combine all reviewer events (approvals, changes requested, reviews, issue comments) into a single group
  // Tag each event with its type so we can render them correctly
  const allReviewerEvents = [
    ...summary.approvals.events.map(event => ({event, type: 'approval' as const})),
    ...summary.changesRequested.events.map(event => ({event, type: 'changes-requested' as const})),
    ...summary.comments.events.map(event => ({event, type: 'review' as const})),
    ...summary.issueComments.events.map(event => ({event, type: 'issue-comment' as const})),
    // TODO: once these groups are a bit more stable, we should have them grouped and sorted on the backend
  ].sort((a, b) => new Date(a.event.timestamp).getTime() - new Date(b.event.timestamp).getTime())

  if (allReviewerEvents.length > 0) {
    const label = buildReviewerActivityLabel(
      summary.approvals.events,
      summary.changesRequested.events,
      summary.comments.events,
      summary.issueComments.events,
    )

    items.push({
      label,
      type: 'reviewer-activity',
      count: allReviewerEvents.length,
      events: allReviewerEvents,
    })
  }

  // Code changes (commits, force pushes, base changes)
  const codeChangeEvents: TaggedEvent[] = [
    ...summary.commits.events.map(event => ({type: 'commit', event})),
    ...summary.forcePushed.events.map(event => ({type: 'force-pushed', event})),
    ...summary.baseChanged.events.map(event => ({type: 'base-changed', event})),
    // TODO: once these groups are a bit more stable, we should have them grouped and sorted on the backend
  ].sort((a, b) => new Date(a.event.timestamp).getTime() - new Date(b.event.timestamp).getTime())

  if (codeChangeEvents.length > 0) {
    let commitRangeLink: string | undefined

    // Build link to Files tab scoped to commit range if there are commits
    if (summary.commits.events.length > 0) {
      const firstCommit = summary.commits.events[0] as CommitTimelineEvent
      const lastCommit = summary.commits.events[summary.commits.events.length - 1] as CommitTimelineEvent

      if (firstCommit?.additionalInfo.baseSha && lastCommit?.additionalInfo.sha) {
        commitRangeLink = `${changesUrl}/${firstCommit.additionalInfo.baseSha}..${lastCommit.additionalInfo.sha}`
      }
    }

    const label = buildCodeChangesLabel(summary.commits.events, summary.forcePushed.events, summary.baseChanged.events)

    items.push({
      label,
      type: 'code-changes',
      count: codeChangeEvents.length,
      events: codeChangeEvents,
      link: commitRangeLink,
      linkText: 'View commit changes',
    })
  }

  // PR state changes (closed, reopened, draft status, merged)
  const stateChangeEvents: TaggedEvent[] = [
    ...summary.closed.events.map(event => ({type: 'closed', event})),
    ...summary.reopened.events.map(event => ({type: 'reopened', event})),
    ...summary.markedAsDraft.events.map(event => ({type: 'marked-as-draft', event})),
    ...summary.readyForReview.events.map(event => ({type: 'ready-for-review', event})),
    ...summary.merged.events.map(event => ({type: 'merged', event})),
    // TODO: once these groups are a bit more stable, we should have them grouped and sorted on the backend
  ].sort((a, b) => new Date(a.event.timestamp).getTime() - new Date(b.event.timestamp).getTime())

  if (stateChangeEvents.length > 0) {
    const label = buildStateChangesLabel(
      summary.closed.events,
      summary.reopened.events,
      summary.markedAsDraft.events,
      summary.readyForReview.events,
      summary.merged.events,
    )

    items.push({
      label,
      type: 'state-changes',
      count: stateChangeEvents.length,
      events: stateChangeEvents,
    })
  }

  // PR metadata changes (labels, review requests, description)
  // Note: For labeled and review requested events, we preserve the specific type from additionalInfo
  const metadataChangeEvents: TaggedEvent[] = [
    ...summary.labeled.events.map(event => ({
      type: event.additionalInfo?.eventType === 'unlabeled' ? 'unlabeled' : 'labeled',
      event,
    })),
    ...summary.reviewRequested.events.map(event => ({
      type:
        event.additionalInfo?.eventType === 'review_request_removed' ? 'review-request-removed' : 'review-requested',
      event,
    })),
    ...summary.descriptionUpdated.events.map(event => ({type: 'description-updated', event})),
    // TODO: once these groups are a bit more stable, we should have them grouped and sorted on the backend
  ].sort((a, b) => new Date(a.event.timestamp).getTime() - new Date(b.event.timestamp).getTime())

  if (metadataChangeEvents.length > 0) {
    const label = buildMetadataChangesLabel(
      summary.labeled.events,
      summary.reviewRequested.events,
      summary.descriptionUpdated.events,
    )

    items.push({
      label,
      type: 'metadata-changes',
      count: metadataChangeEvents.length,
      events: metadataChangeEvents,
    })
  }

  return items
}

/** Get the verb for the event type */
const EVENT_VERB_MAP: Record<string, string> = {
  approval: 'approved',
  'changes-requested': 'requested changes',
  review: 'reviewed',
  'issue-comment': 'commented',
  comment: 'commented',
  commit: 'committed',
  closed: 'closed',
  reopened: 'reopened',
  'marked-as-draft': 'marked as draft',
  'ready-for-review': 'marked ready for review',
  labeled: 'labeled',
  'review-requested': 'requested review',
  'review-dismissed': 'dismissed review',
  'description-updated': 'description updated',
  'force-pushed': 'force pushed',
  merged: 'merged',
  'base-changed': 'changed base branch',
}

export function getEventVerb(eventType: string): string {
  return EVENT_VERB_MAP[eventType] ?? ''
}

/** Format a truncated list of teams, showing first N teams and "X others" if more */
export function formatTruncatedTeams(teams: string[], maxTeams: number = 2): string {
  if (teams.length === 0) {
    return ''
  }

  const displayTeams = teams.slice(0, maxTeams)
  const remainingCount = teams.length - displayTeams.length

  let teamList: string
  if (displayTeams.length === 2 && remainingCount === 0) {
    teamList = displayTeams.map(t => `@${t}`).join(' and ')
  } else {
    teamList = displayTeams.map(t => `@${t}`).join(', ')
    if (remainingCount > 0) {
      teamList += `, and ${remainingCount} ${remainingCount === 1 ? 'other' : 'others'}`
    }
  }

  return teamList
}

/**
 * Build a summary label for the reviewer activity group.
 * Combines approvals, changes requested, reviews, and issue comments.
 * @example "3 reviews submitted, 2 approved and 1 requested changes with 5 total comments"
 * @example "No reviews submitted, 3 total comments"
 */
function buildReviewerActivityLabel(
  approvals: TimelineEvent[],
  changesRequested: TimelineEvent[],
  reviews: TimelineEvent[],
  issueComments: TimelineEvent[],
): string {
  const total = approvals.length + changesRequested.length + reviews.length

  // Main count
  const mainCount = total === 0 ? 'No reviews submitted' : `${total} ${total === 1 ? 'review' : 'reviews'} submitted`

  // Specific counts for approvals and changes requested (not comments)
  const specificCounts: string[] = []

  if (approvals.length > 0) {
    specificCounts.push(`${approvals.length} approved`)
  }

  if (changesRequested.length > 0) {
    specificCounts.push(`${changesRequested.length} requested changes`)
  }

  let result: string
  if (specificCounts.length === 0) {
    result = mainCount
  } else {
    result = `${mainCount}, ${specificCounts.join(' and ')}`
  }

  // Sum up comment counts from all review events (approvals, changes requested, and reviews)
  const allReviewEvents = [...approvals, ...changesRequested, ...reviews]
  const totalComments = allReviewEvents.reduce((sum, event) => {
    const reviewEvent = event as ReviewTimelineEvent
    return sum + (reviewEvent.additionalInfo?.commentCount ?? 0)
  }, 0)

  // Add issue comments to the total comment count
  const totalCommentsIncludingIssue = totalComments + issueComments.length

  // Add comment count if there are any comments
  if (totalCommentsIncludingIssue > 0) {
    // Use comma for "No reviews submitted" case, "with" otherwise
    const connector = total === 0 ? ',' : ' with'
    result += `${connector} ${totalCommentsIncludingIssue} total ${totalCommentsIncludingIssue === 1 ? 'comment' : 'comments'}`
  }

  return result
}

/**
 * Build a summary label for the PR state changes group.
 * Tracks lifecycle transitions: closed, reopened, marked as draft, ready for review, merged.
 * @example "This PR was closed, reopened, and merged"
 * @example "This PR was merged"
 */
function buildStateChangesLabel(
  closed: TimelineEvent[],
  reopened: TimelineEvent[],
  markedAsDraft: TimelineEvent[],
  readyForReview: TimelineEvent[],
  merged: TimelineEvent[],
): string {
  const stateChanges: string[] = []

  if (closed.length > 0) {
    stateChanges.push('closed')
  }

  if (reopened.length > 0) {
    stateChanges.push('reopened')
  }

  if (markedAsDraft.length > 0) {
    stateChanges.push('marked as draft')
  }

  if (readyForReview.length > 0) {
    stateChanges.push('marked ready for review')
  }

  if (merged.length > 0) {
    stateChanges.push('merged')
  }

  const stateText = formatListWithAnd(stateChanges)
  return `This PR was ${stateText}`
}

/**
 * Build a summary label for the PR metadata changes group.
 * Tracks label changes, review request changes, and description updates.
 * @example "3 label changes, 2 review request changes, and 1 description update"
 * @example "5 label changes"
 */
function buildMetadataChangesLabel(
  labeled: TimelineEvent[],
  reviewRequested: TimelineEvent[],
  descriptionUpdated: TimelineEvent[],
): string {
  const metadataChanges: string[] = []

  if (labeled.length > 0) {
    metadataChanges.push(`${labeled.length} label ${labeled.length === 1 ? 'change' : 'changes'}`)
  }

  if (reviewRequested.length > 0) {
    metadataChanges.push(
      `${reviewRequested.length} review request ${reviewRequested.length === 1 ? 'change' : 'changes'}`,
    )
  }

  if (descriptionUpdated.length > 0) {
    metadataChanges.push(
      `${descriptionUpdated.length} description ${descriptionUpdated.length === 1 ? 'update' : 'updates'}`,
    )
  }

  return formatListWithAnd(metadataChanges)
}

/**
 * Build a summary label for the code changes group.
 * Tracks commits, force pushes, and base branch changes.
 * @example "3 new commits, 2 force pushes, and 1 base change"
 * @example "5 new commits"
 */
function buildCodeChangesLabel(
  commits: TimelineEvent[],
  forcePushed: TimelineEvent[],
  baseChanged: TimelineEvent[],
): string {
  const parts: string[] = []

  if (commits.length > 0) {
    parts.push(`${commits.length} new ${commits.length === 1 ? 'commit' : 'commits'}`)
  }

  if (forcePushed.length > 0) {
    parts.push(`${forcePushed.length} force ${forcePushed.length === 1 ? 'push' : 'pushes'}`)
  }

  if (baseChanged.length > 0) {
    parts.push(`${baseChanged.length} base ${baseChanged.length === 1 ? 'change' : 'changes'}`)
  }

  return formatListWithAnd(parts)
}

/** Format a list with commas and "and" before the last item */
function formatListWithAnd(items: string[]): string {
  if (items.length === 0) {
    return ''
  }
  if (items.length === 1) {
    return items[0] ?? ''
  }
  if (items.length === 2) {
    return items.join(' and ')
  }
  const lastItem = items[items.length - 1]
  return `${items.slice(0, -1).join(', ')}, and ${lastItem ?? ''}`
}
